import { searchPatch } from "@/lib/search";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { SearchField } from "@/components/app/patterns/search-field";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { conversationTitle } from "@alm/domain/messaging-names";
import { inboxQuery } from "@/lib/messaging/live/queries";
import type { ConversationRow, SharedFile } from "@/lib/messaging/history/types";
import { FilesKindFilter } from "../-components/details/files-kind-filter";
import { isFileFilter, type FileFilter } from "../-components/details/files-kinds";
import { SharedFiles } from "../-components/details/shared-files";
import { useSharedFiles } from "../-components/details/use-shared-files";
import { InboxBackButton } from "../-components/home/inbox-back-button";

export const Route = createFileRoute("/_authenticated/messaging/_inbox/files")({
  validateSearch: (search: Record<string, unknown>): { kind?: FileFilter; q?: string } => ({
    kind: isFileFilter(search.kind) && search.kind !== "all" ? search.kind : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q.slice(0, 120) : undefined,
  }),
  component: FilesPage,
});

/** Where a file was shared, worded for a line under its name. */
function placeOf(row: ConversationRow | undefined): string | null {
  if (!row) return null;
  if (row.kind === "direct") return "a direct message";
  return row.kind === "channel" ? `#${conversationTitle(row)}` : conversationTitle(row);
}

/** Every file shared in the person's conversations, to find one again by name or kind. */
function FilesPage() {
  const { kind = "all", q = "" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const search = useSearchParamText(q || undefined, (next) => patch({ q: next }));
  const inbox = useQuery(inboxQuery());
  const files = useSharedFiles({ filter: kind, query: q });
  const byId = useMemo(() => new Map((inbox.data ?? []).map((row) => [row.id, row])), [inbox.data]);

  const setKind = (next: FileFilter) =>
    void navigate({
      search: (prev) => ({ ...prev, kind: next === "all" ? undefined : next }),
      replace: true,
    });
  const filtered = q !== "" || kind !== "all";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b px-3 py-2 md:px-4">
        <InboxBackButton />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold">Files</h1>
          <p className="text-muted-foreground truncate text-xs">
            Shared in your conversations, newest first
          </p>
        </div>
      </header>
      <div className="flex flex-col gap-2 border-b px-3 py-2.5 sm:flex-row sm:items-center md:px-4">
        <SearchField value={search.text} onChange={search.change} placeholder="Search by name…" />
        <FilesKindFilter value={kind} onChange={setKind} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 md:px-4">
        <div className="mx-auto max-w-3xl">
          <SharedFiles
            files={files}
            headingLevel={2}
            settling={search.settling}
            whereOf={(file: SharedFile) => placeOf(byId.get(file.conversationId))}
            empty={
              filtered
                ? { title: "No files match", description: "Try another name or kind." }
                : {
                    title: "No files yet",
                    description:
                      "Pictures, videos and documents shared in your conversations show up here.",
                  }
            }
          />
        </div>
      </div>
    </div>
  );
}
